import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../../services/api';
import CoinItem from '../../components/common/CoinItem';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorDisplay from '../../components/common/ErrorDisplay';
import EmptyState from '../../components/common/EmptyState';
import UserSearchDropdown from '../../components/features/UserSearchDropdown';
import BackButton from '../../components/common/BackButton';
import { formatRelativeTime } from '../../utils/dateUtils';
import { stripHTML } from '../../utils/htmlSanitizer';
import './UserCoins.css';

function UserCoins() {
  const auth = useAuth();
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [allTransactions, setAllTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);

  // Filter states
  const [direction, setDirection] = useState('received'); // 'received' or 'sent'
  const [coinTypeFilter, setCoinTypeFilter] = useState('both'); // 'individual', 'organizational', 'both'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]); // Array of selected senders/receivers
  const [descriptionSearch, setDescriptionSearch] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Check if this is the current user's profile
  const isOwnProfile = auth.user?.profile?.sub === userId;

  // Apply URL query parameter for coin type filter
  useEffect(() => {
    const typeParam = searchParams.get('type');
    if (typeParam === 'individual' || typeParam === 'organizational') {
      setCoinTypeFilter(typeParam);
    }
  }, [searchParams]);

  // Fetch user and all transactions
  useEffect(() => {
    const fetchUserAndTransactions = async () => {
      if (!auth.isAuthenticated || !auth.user?.id_token) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const idToken = auth.user.id_token;

        // Fetch user profile and transactions based on direction
        const [userData, transactionsData] = await Promise.all([
          apiService.getUserProfile(idToken, userId),
          apiService.getUserTransactionsById(idToken, userId, {
            type: direction,
            limit: 1000 // Get all transactions for filtering
          })
        ]);

        setUser(userData);
        // Only show completed (non-pending) transactions
        const completedTransactions = (transactionsData.transactions || []).filter(
          tx => !tx.pending
        );
        setAllTransactions(completedTransactions);
        setFilteredTransactions(completedTransactions);
      } catch (err) {
        console.error('Error fetching user coins:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserAndTransactions();
    }
  }, [userId, auth.isAuthenticated, auth.user, direction]);

  // Apply filters whenever filter criteria change
  useEffect(() => {
    let filtered = [...allTransactions];

    // Filter by coin type
    if (coinTypeFilter !== 'both') {
      filtered = filtered.filter(tx => tx.coin_type === coinTypeFilter);
    }

    // Filter by date range
    if (startDate) {
      const startDateTime = new Date(startDate).getTime();
      filtered = filtered.filter(tx => new Date(tx.created_at).getTime() >= startDateTime);
    }
    if (endDate) {
      // Add 1 day to include the entire end date
      const endDateTime = new Date(endDate).getTime() + 86400000;
      filtered = filtered.filter(tx => new Date(tx.created_at).getTime() < endDateTime);
    }

    // Filter by users (senders when receiving, receivers when sending)
    if (selectedUsers.length > 0) {
      const userIds = selectedUsers.map(s => s.user_id);
      filtered = filtered.filter(tx =>
        direction === 'received'
          ? userIds.includes(tx.sender_id)
          : userIds.includes(tx.receiver_id)
      );
    }

    // Filter by description text
    if (descriptionSearch.trim()) {
      const searchLower = descriptionSearch.toLowerCase().trim();
      filtered = filtered.filter(tx => {
        const plainText = stripHTML(tx.description || '').toLowerCase();
        return plainText.includes(searchLower);
      });
    }

    setFilteredTransactions(filtered);
  }, [allTransactions, coinTypeFilter, startDate, endDate, selectedUsers, descriptionSearch, direction]);

  const handleUserSelect = (user) => {
    if (!user) return;

    // Check if user is already selected
    const isAlreadySelected = selectedUsers.some(s => s.user_id === user.user_id);

    if (!isAlreadySelected) {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const handleRemoveUser = (userId) => {
    setSelectedUsers(selectedUsers.filter(s => s.user_id !== userId));
  };

  const clearFilters = () => {
    setCoinTypeFilter('both');
    setStartDate('');
    setEndDate('');
    setSelectedUsers([]);
    setDescriptionSearch('');
  };

  const hasActiveFilters = coinTypeFilter !== 'both' || startDate || endDate || selectedUsers.length > 0 || descriptionSearch;

  if (loading) {
    return (
      <div className="user-coins-container">
        <LoadingSpinner message="Loading coins..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-coins-container">
        <ErrorDisplay
          title="Error Loading Coins"
          message={error}
          onRetry={() => window.history.back()}
        />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-coins-container">
        <div className="error-container">
          <h2>User Not Found</h2>
          <p>The user you're looking for doesn't exist.</p>
          <BackButton label="Go Back" />
        </div>
      </div>
    );
  }

  // Get first name from display name
  const firstName = user.display_name.split(' ')[0];

  return (
    <div className="user-coins-container">
      {/* Header */}
      <div className="user-coins-header">
        <BackButton />
      </div>

      {/* Combined Filters & Coins List */}
      <section className="coins-section">
        {/* Filters Bar */}
        <div className="filters-bar">
          <div className="filters-main-row">
            {/* Direction Toggle */}
            <div className="direction-toggle">
              <button
                className={`direction-btn ${direction === 'received' ? 'active' : ''}`}
                onClick={() => { setDirection('received'); setSelectedUsers([]); }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 19V5M5 12l7 7 7-7" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Received
              </button>
              <button
                className={`direction-btn ${direction === 'sent' ? 'active' : ''}`}
                onClick={() => { setDirection('sent'); setSelectedUsers([]); }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12l7-7 7 7" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Sent
              </button>
            </div>

            {/* Search */}
            <div className="filter-search-wrapper">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                type="text"
                value={descriptionSearch}
                onChange={(e) => setDescriptionSearch(e.target.value)}
                placeholder="Search descriptions..."
                className="filter-search-input"
              />
            </div>

            {/* Coin Type Tabs - Organization filter temporarily disabled */}
            {/* <div className="filter-type-tabs">
              <button
                className={`type-tab ${coinTypeFilter === 'both' ? 'active' : ''}`}
                onClick={() => setCoinTypeFilter('both')}
              >
                All
              </button>
              <button
                className={`type-tab ${coinTypeFilter === 'individual' ? 'active' : ''}`}
                onClick={() => setCoinTypeFilter('individual')}
              >
                <span className="tab-icon">👤</span>
                Individual
              </button>
              <button
                className={`type-tab ${coinTypeFilter === 'organizational' ? 'active' : ''}`}
                onClick={() => setCoinTypeFilter('organizational')}
              >
                <span className="tab-icon">🏢</span>
                Org
              </button>
            </div> */}

            {/* Advanced Toggle & Clear */}
            <div className="filter-actions">
              <button
                className={`filter-toggle-btn ${showAdvancedFilters ? 'active' : ''}`}
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 3v18M3 12h18M8 8l8 8M16 8l-8 8" strokeLinecap="round"/>
                </svg>
                Filters
                {(startDate || endDate || selectedUsers.length > 0) && (
                  <span className="filter-badge">{(startDate ? 1 : 0) + (endDate ? 1 : 0) + selectedUsers.length}</span>
                )}
              </button>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="filter-clear-btn">
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Advanced Filters Panel */}
          {showAdvancedFilters && (
            <div className="filters-advanced-panel">
              <div className="advanced-filter-group">
                <label>Date Range</label>
                <div className="date-range-inputs">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={endDate || undefined}
                    className="date-input"
                  />
                  <span className="date-separator">→</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate || undefined}
                    className="date-input"
                  />
                </div>
              </div>

              <div className="advanced-filter-group">
                <label>{direction === 'received' ? 'From' : 'To'}</label>
                <UserSearchDropdown
                  onSelect={handleUserSelect}
                  placeholder={direction === 'received' ? 'Add sender...' : 'Add recipient...'}
                  searchFn={async (query) => {
                    const idToken = auth.user.id_token;
                    return await apiService.searchUsers(idToken, query, 10);
                  }}
                />
                {selectedUsers.length > 0 && (
                  <div className="selected-senders-list">
                    {selectedUsers.map((user) => (
                      <div key={user.user_id} className="sender-chip">
                        <span className="sender-chip-name">{user.display_name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveUser(user.user_id)}
                          className="sender-chip-remove"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Coins List */}
        <div className="coins-list-content">
          {filteredTransactions.length === 0 ? (
            <EmptyState
              icon={hasActiveFilters ? "🔍" : "🎉"}
              message={hasActiveFilters ? "No coins match your filters" : `No coins ${direction} yet`}
            />
          ) : (
            <div className="coins-list">
              {filteredTransactions.map((tx) => (
                <CoinItem
                  key={tx.tx_id}
                  id={tx.tx_id}
                  coinType={tx.coin_type}
                  senderName={direction === 'received' ? (tx.sender_name || 'Unknown') : (tx.receiver_name || 'Unknown')}
                  senderId={direction === 'received' ? tx.sender_id : tx.receiver_id}
                  message={tx.description || 'No description'}
                  timestamp={formatRelativeTime(tx.created_at)}
                  senderEmail={direction === 'received' ? tx.sender_email : tx.receiver_email}
                  senderJobTitle={direction === 'received' ? tx.sender_job_title : tx.receiver_job_title}
                  direction={direction}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default UserCoins;
